import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import Api from '../../data'

import {
    getStartTestPayload,
    loadScriptByURL,
    handleFullscreen,
    updateTestStatus,
    updateInstructions,
    updatePropsOnGiveTestSuccess,
    getDeviceInfo,
    getTestEndTime,
    getUrlParameter,
    getErrorMsg,
    generateUuid,
} from './helper'

import {
    pageLoadSuccess,
    startTestSuccess,
    startTestFailure,
    resumeTestSuccess,
    resumeTestFailure,
} from './analytics'
import { EVENT_NAMES } from 'constants/eventNames'
import { trackEvent } from 'utils/analyticsService'
import { ANALYTICS_KEYS } from 'constants/analytics-keys'
import CONFIG from '../../config'
import { ANTHE_EXAM_TYPES, ANTHE_TEST_TYPES } from 'constants/examTypeConstants'

const SITE_KEY = CONFIG.recaptchaSiteKey

function useInstructionsData(props) {
    const { i18n } = useTranslation()
    const [loading, setLoading] = useState(false)
    const [isError, setIsError] = useState(false)
    const [buttonText, setButtonText] = useState('')
    const [connection, setConnection] = useState('')
    const [param, setParam] = useState('MAN_117430050007_8')
    const [allTestStatus, setAllTestStatus] = useState()
    const [testId, setTestId] = useState()
    const [errorMsg, setErrorMsg] = useState('')
    const [isSecure, setIsSecure] = useState(true)
    const [redirectToTestPanel, setRedirectToTestPanel] = useState(false)
    const [redirectToResults, setRedirectToResults] = useState(false)
    const [showStartPreviewBtn, setShowStartPreviewBtn] = useState(false)
    const [showLoginButton, setShowLoginButton] = useState(false)
    const [shouldResumeTest, setShouldResumeTest] = useState(false)

    useEffect(() => {
        if (shouldResumeTest && allTestStatus) {
            const deviceInfo = getDeviceInfo(props)

            Api.resumeTest(allTestStatus[param]['userTestId'], deviceInfo)
                .then((res) => res.data)
                .then((res) => {
                    handleGiveTestSuccess(res)
                })
        }
    }, [shouldResumeTest, allTestStatus])

    useEffect(() => {
        window.addEventListener('online', onToggleOnline)
        window.addEventListener('offline', onToggleOffline)

        return () => {
            window.removeEventListener('online', onToggleOnline)
            window.removeEventListener('offline', onToggleOffline)
        }
    }, [])

    function handleError(error) {
        const message = getErrorMsg(error)
        setErrorMsg(message)
        setIsError(true)
        setLoading(false)
    }

    function onToggleOffline() {
        localStorage.setItem('offline', 'true')
        setConnection('offline')
    }

    function onToggleOnline() {
        if (localStorage.getItem('offline') === 'true') {
            localStorage.setItem('offline', 'false')
            setConnection('online')
        }
    }

    async function fetchGiveTestData(isStartTest) {
        const deviceInfo = getDeviceInfo(props)

        if (isStartTest) {
            const data = getStartTestPayload(props, testId)

            const response = await Api.startNewTest(data, deviceInfo).then((res) => {
                if (res.response?.status === 401) {
                    return res.response
                } else {
                    return res.data
                }
            })
            return response
        } else {
            return Api.resumeTest(
                allTestStatus[param]['userTestId'],
                deviceInfo,
            ).then((res) => res.data)
        }
    }

    function handleGiveTestSuccess(res) {
        const isStartTest =
            buttonText === 'Start Test' || buttonText === 'टेस्ट प्रारम्भ करें'
        try {
            handleFullscreen()
            updatePropsOnGiveTestSuccess(
                props,
                res,
                isStartTest ? 0 : res.data.consumedTime / 60,
            )
            setLoading(false)
            setRedirectToTestPanel(true)
            sessionStorage.setItem('isRedirected', true)
            sessionStorage.setItem('timer', '')
            isStartTest && startTestSuccess(props, res)
            !isStartTest && resumeTestSuccess(props, allTestStatus, param)
        } catch (error) {
            handleError(error)
        }
    }

    async function giveTest() {
        const isStartTest =
            buttonText === 'Start Test' || buttonText === 'टेस्ट प्रारम्भ करें'
        if (isStartTest) {
            trackEvent(EVENT_NAMES.TESTPLAYER_START_TEST, '', {
                [ANALYTICS_KEYS.TEST_ID]: props.instructions?.testData?.id,
                [ANALYTICS_KEYS.ROLL_NUMBER]: props.queryparam?.roll_no,
            })
        }
        setLoading(true)
        props.setTestDataLoadedStatus(false)
        props.setGlobalTestStatus(param)
        !isStartTest && props.setIsExistingTestParam(true)
        await fetchTestStatus(
            props.queryparam,
            { data: props.instructions },
            param,
            false,
        )
        try {
            const res = await fetchGiveTestData(isStartTest)
            if (res?.status === 401) {
                setShouldResumeTest(true)
                return
            }
            if (res) {
                handleGiveTestSuccess(res)
            } else {
                setErrorMsg('Something went wrong!')
                setIsError(true)
                setLoading(false)
            }
        } catch (error) {
            isStartTest && startTestFailure(props, getErrorMsg(error))
            !isStartTest &&
                resumeTestFailure(props, allTestStatus, param, getErrorMsg(error))
            handleError(error)
        }
    }

    const handleVerifyCaptcha = () => {
        try {
            window.grecaptcha.ready(() => {
                window.grecaptcha
                    .execute(SITE_KEY, { action: 'submit' })
                    .then((token) => {
                        Api.verifyCaptcha(token)
                            .then((response) => {
                                if (
                                    response?.data?.score <=
                                    CONFIG.recaptchaSiteThreshold
                                ) {
                                    setIsSecure(false)
                                }
                            })
                            .catch((error) => {
                                console.log(error)
                            })
                    })
                    .catch((error) => {
                        console.error(
                            'Recaptcha (null value) issue in execute window.grecaptcha ',
                            error,
                        )
                    })
            })
        } catch (error) {
            handleError(error)
        }
    }

    const redirection = (queryParam) => {
        const isMock = JSON.parse(sessionStorage.getItem('is_mock'))
        if (
            (!isMock &&
                queryParam?.exam_type &&
                ANTHE_EXAM_TYPES.includes(queryParam?.exam_type)) ||
            ANTHE_TEST_TYPES.includes(queryParam?.exam_type)
        ) {
            // setisRedirect(false)
            props.history.push({ pathname: '/success' })
        } else {
            setRedirectToResults(true)
        }
    }

    function fetchPreviewData({ tp_preview_token, test_id }) {
        props.setMockTestSubmit(false)
        sessionStorage.setItem('tp_preview_token', tp_preview_token)
        sessionStorage.setItem('test_id', test_id)
        // We can clear old 'auth_token' token for iacst/anthe in case user is on test preview
        localStorage.setItem('auth_token', '')

        Api.getTestQuestionAnswerData('', test_id)
            .then((res) => res.data)
            .then((res) => {
                props.setTestPreviewData(res)
                setShowStartPreviewBtn(true)
                if (res?.data?.testShortCode) {
                    fetchInstructions(res.data, true)
                } else {
                    setIsError(true)
                }
            })
        props.hideTimer(true)
    }

    async function fetchInstructions(obj, isPreview = false) {
        try {
            const res = await await Api.getInstructionDataByShortCode(
                isPreview ? obj.testShortCode : obj.test_short_code,
            ).then((res) => res.data)

            await updateInstructions(
                props,
                obj,
                res,
                isPreview,
                setParam,
                setTestId,
                fetchTestStatus,
            )
            const availableLanguages =
                res.data?.instruction?.language?.split(',') || []
            const needToResetLanguage = !availableLanguages.includes(
                props.current_language,
            )
            if (needToResetLanguage) {
                i18n.changeLanguage(
                    availableLanguages && availableLanguages.length
                        ? availableLanguages[0]
                        : 'en',
                )
                props.updateCurrentLanguage(
                    availableLanguages && availableLanguages.length
                        ? availableLanguages[0]
                        : 'en',
                )
            }
        } catch (error) {
            if (error.response != undefined && error.response.status >= 400) {
                setErrorMsg(getErrorMsg(error))
                setIsError(true)
            } else {
                setIsError(true)
            }
        }
    }

    async function fetchTestStatus(
        obj,
        instructionsData,
        param,
        logPageLoad = true,
    ) {
        const res = await Api.getTestStatus(
            props.queryparam.test_short_code,
            props.queryparam.roll_no,
            param,
            obj.test_attempt,
        ).then((r) => r.data)
        if (!res) {
            setErrorMsg('Something went wrong!')
            setIsError(true)
            setLoading(false)
            return
        }
        props.setLoadingPercent(0)
        await updateTestStatus(
            props.setTestStatus,
            obj,
            res,
            setButtonText,
            redirection,
            setAllTestStatus,
            param,
            props.setIsExistingTestParam,
            setErrorMsg,
            setIsError,
        )
        logPageLoad && pageLoadSuccess(props, instructionsData)
        return
    }

    function fetchData() {
        props.setMockTestSubmit(false)
        props.updateCalculatedTimeLeft && props.updateCalculatedTimeLeft(0)

        i18n.changeLanguage(props.current_language)

        props.hideTimer && props.hideTimer(true)
        props.onQuestionLoad && props.onQuestionLoad([])
        const authToken =
            getUrlParameter('schedule_data') || localStorage.getItem('auth_token')
        if (authToken) {
            localStorage.setItem('auth_token', authToken)
            // We can clear old 'tp_preview_token' and 'test_id' token for test preview in case user is on iacst/anthe
            sessionStorage.setItem('tp_preview_token', '')
            sessionStorage.setItem('test_id', '')
        }
        let instruction_url = `${window.location.protocol}//${window.location.host}/?schedule_data=${authToken}`
        props.setBaseUrl && props.setBaseUrl(instruction_url)
        if (SITE_KEY) {
            loadScriptByURL(
                'recaptcha-key',
                `https://www.google.com/recaptcha/api.js?render=${SITE_KEY}`,
                handleVerifyCaptcha,
            )
        }
        if (!localStorage.getItem('browser-uuid')) {
            const uuid = generateUuid()
            localStorage.setItem('browser-uuid', uuid)
        }
        Api.init()
            .then((res) => {
                res.data.ipAddress = res.headers['x-ip-address']
                return res.data
            })
            .then((res) => {
                let obj = res.data
                localStorage.setItem('roll_no', JSON.stringify(obj.roll_no))
                sessionStorage.setItem(
                    'is_mock',
                    obj.test_type.toLowerCase() !== 'scholarship',
                )
                sessionStorage.setItem('isSubmitted', false)
                props.setIpAddress && props.setIpAddress(res.ipAddress)
                props.setAntheData && props.setAntheData('')
                props.setHeaderVisibility && props.setHeaderVisibility(true)
                props.setQueryParam && props.setQueryParam(obj)
                const CurrentDateTime = new Date()
                const testEndDateTime = obj.vendor_slot ? getTestEndTime(obj) : null
                if (
                    testEndDateTime &&
                    CurrentDateTime.getTime() >= new Date(testEndDateTime).getTime()
                ) {
                    setErrorMsg('Your test date/slot has expired.')
                    setIsError(true)
                } else {
                    fetchInstructions(obj)

                    trackEvent(EVENT_NAMES.TESTPLAYER_SESSION_START, '', {
                        [ANALYTICS_KEYS.ROLL_NUMBER]: obj?.roll_no,
                    })
                }
            })
            .catch((error = {}) => {
                localStorage.clear()
                setLoading(false)
                if (error.response !== undefined && error.response.status >= 400) {
                    setShowLoginButton(
                        error?.response?.data?.data?.show_login_button || false,
                    )
                    setIsError(true)
                    setErrorMsg(getErrorMsg(error))
                } else {
                    setIsError(true)
                    setErrorMsg('Invalid token! Please login & Restart the test!')
                }
            })
    }

    return {
        loading,
        isError,
        errorMsg,
        isSecure,
        redirectToTestPanel,
        redirectToResults,
        connection,
        getUrlParameter,
        showStartPreviewBtn,
        showLoginButton,
        buttonText,
        giveTest,
        fetchData,
        fetchPreviewData,
        setRedirectToTestPanel,
    }
}

export default useInstructionsData
